import React, { useState, useEffect } from "react";
import { useAuth } from "../../../Common/Configurations/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import apiService from "../../../Common/Configurations/APIService";
import config from "../../../Common/Configurations/APIConfig";
import Header from "../../../Common/Layout/Header";
import CorporateTabs from "../../AllTabs/CorporateTabs";
import { format } from "date-fns";
import Modal from "react-bootstrap/Modal";
import { toast } from "react-toastify";

const CorporateWallet = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [agents, setAgents] = useState([]);
  const [filteredAgents, setFilteredAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);

  // Search and filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState([]);

  // Wallet Modal states
  const [walletShow, setWalletShow] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [amount, setAmount] = useState("");
  const [walletErrors, setWalletErrors] = useState({});
  const [walletBtnLoading, setWalletBtnLoading] = useState(false);

 
  const GetData = async (currentPage = 1) => {
    if (!user) return;
    setLoading(true);
    try {
      const apiUrl = `${config.GETCOMPANYCORPORATE}/${user.cid}?PageNumber=${currentPage}&PageSize=${pageSize}`;
      const response = await apiService.getWithParams(apiUrl);

      const agentsData = response.data || [];
      setAgents(agentsData);
      setFilteredAgents(agentsData);
      setTotalPages(response.totalPages || 0);
      setTotalRecords(response.totalRecords || 0);
      setPageNumber(response.pageNumber || 1);
    } catch (error) {
      console.error("Error fetching agents:", error);
      toast.error("Failed to fetch agents.");
      setAgents([]);
      setFilteredAgents([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!authLoading && user) {
      GetData(pageNumber);
    }
  }, [user, authLoading, pageNumber]);

  // Generate search suggestions
  const generateSuggestions = (searchValue) => {
    if (!searchValue.trim()) {
      setSuggestions([]);
      return;
    }
    const searchLower = searchValue.toLowerCase();
    const companyMatches = agents
      .filter((agent) =>
        agent.companyName?.toLowerCase().includes(searchLower)
      )
      .slice(0, 5);
    setSuggestions(companyMatches);
  };

  // Handle search
  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    generateSuggestions(value);
    setShowSuggestions(true);

    if (value.trim() === "") {
      setFilteredAgents(agents);
      setShowSuggestions(false);
    } else {
      const filtered = agents.filter((agent) =>
        agent.companyName?.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredAgents(filtered);
    }
  };

  const handleSuggestionClick = (agent) => {
    setSearchTerm(agent.companyName);
    setFilteredAgents([agent]);
    setShowSuggestions(false);
  };

  const handleInputBlur = () => {
    setTimeout(() => setShowSuggestions(false), 200);
  };

  // Pagination functions
  const GetPageData = (number) => {
    if (pageNumber !== number) GetData(number);
  };

  const renderPageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1).map(
    (number) => (
      <li
        className={`page-item ${pageNumber === number ? "active" : ""}`}
        key={number}
        onClick={() => GetPageData(number)}
      >
        <Link className="page-link">{number}</Link>
      </li>
    )
  );

  // Wallet Modal Functions
  const handleWalletModalOpen = (agent) => {
    setSelectedAgent(agent);
    setWalletShow(true);
  };

  const handleWalletModalClose = () => {
    setWalletShow(false);
    setSelectedAgent(null);
    setAmount("");
    setWalletErrors({});
    setWalletBtnLoading(false);
  };

  const validateWalletForm = () => {
    const newErrors = {};
    if (!amount || isNaN(amount) || Number(amount) <= 0) {
      newErrors.amount = "Please enter a valid amount greater than 0.";
    }
    setWalletErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSaveWalletBalance = async () => {
    if (!validateWalletForm()) {
      return;
    }
    setWalletBtnLoading(true);

    const walletData = {
       CompanyId: selectedAgent.companyId,
      Amount: Number(amount),
      Status: "CREDITED",
    };
    console.log("Wallet Data to be sent:", walletData);

    try {
      // Assuming you have an endpoint for creating a wallet transaction.
      // Replace 'config.CREATECOMPANYWALLET' with your actual API endpoint from APIConfig.js
      const response = await apiService.create(config.CREATECOMPANYWALLET, walletData);
      if (response) {
        handleWalletModalClose();
        GetData(pageNumber); // Refresh the client list to show the updated balance
      } else {
        toast.error("Failed to add balance.");
      }
    } catch (error) {
      console.error("Error adding wallet balance:", error);
      toast.error("An error occurred while adding balance.");
    } finally {
      setWalletBtnLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return format(new Date(dateString), "dd MMM, yyyy");
  };

  if (authLoading) return <p>Loading...</p>;
  if (!user) {
    navigate("/");
    return null;
  }

  return (
    <>
      <Header />
      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            <div className="card">
              <div className="p-2">
                <CorporateTabs pageName="CORPORATEWALLET" />
              </div>

              <div className="card-body">
                <div className="row mb-4">
                  <div className="col-12">
                    <div className="d-flex justify-content-between align-items-center mb-4">
                      <h5 className="card-title mb-0">Corporate Wallet Management</h5>
                    </div>

                    {/* Search Bar */}
                    <div className="col-md-6">
                      <div style={{ position: "relative" }}>
                        <i
                          className="ri-search-line"
                          style={{
                            position: "absolute",
                            left: "12px",
                            top: "50%",
                            transform: "translateY(-50%)",
                            zIndex: 10,
                            color: "#6c757d",
                          }}
                        ></i>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Search by company name..."
                          value={searchTerm}
                          onChange={handleSearch}
                          onFocus={() => setShowSuggestions(true)}
                          onBlur={handleInputBlur}
                          style={{
                            height: "45px",
                            paddingLeft: "40px",
                          }}
                        />
                        {showSuggestions && suggestions.length > 0 && (
                          <div
                            style={{
                              position: "absolute",
                              width: "100%",
                              marginTop: "4px",
                              border: "1px solid #dee2e6",
                              backgroundColor: "white",
                              borderRadius: "6px",
                              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                              zIndex: 1000,
                            }}
                          >
                            {suggestions.map((agent) => (
                              <div
                                key={agent.companyId}
                                style={{
                                  padding: "8px 12px",
                                  cursor: "pointer",
                                }}
                                onMouseDown={() => handleSuggestionClick(agent)}
                              >
                                {agent.companyName}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Table Section */}
                <div className="mt-4">
                  <div className="table-responsive table-card">
                    <table className="table align-middle table-hover">
                      <thead className="table-light">
                        <tr>
                          <th>S.No</th>
                          <th>Company Name</th>
                          <th>Contact</th>
                          <th>Wallet Balance</th>
                          <th>Plan</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loading ? (
                          <tr>
                            <td colSpan="7" className="text-center py-4">
                              Loading agents...
                            </td>
                          </tr>
                        ) : filteredAgents.length > 0 ? (
                          filteredAgents.map((agent, index) => (
                            <tr key={agent.companyId}>
                              <td>{(pageNumber - 1) * pageSize + index + 1}</td>
                              <td>
                                <div>
                                  <strong>{agent.companyName}</strong>
                                </div>
                                <small className="text-muted">
                                  {agent.brandName || "-"}
                                </small>
                              </td>
                              <td>
                                <div>{agent.email}</div>
                                <small className="text-muted">
                                  {agent.phoneNumber}
                                </small>
                              </td>
                              <td>
                                <strong className="text-success">
                                  {agent.currency || 'INR'} {agent.walletBalance?.toFixed(2) || "0.00"}
                                </strong>
                              </td>
                              <td>
                                <span className="badge bg-primary">
                                  {agent.planType || "N/A"}
                                </span>
                              </td>
                              <td>
                                <span
                                  className={`badge ${
                                    agent.isActive ? "bg-success" : "bg-danger"
                                  }`}
                                >
                                  {agent.isActive ? "Active" : "Inactive"}
                                </span>
                              </td>
                              <td>
                                <button
                                  className="btn btn-sm btn-primary"
                                  onClick={() => handleWalletModalOpen(agent)}
                                >
                                  <i className="ri-add-line me-1"></i>
                                  Add Balance
                                </button>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="7" className="text-center py-4">
                              No agents found.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Pagination section */}
                {filteredAgents.length > 0 && (
                  <div className="align-items-center mt-4 pt-2 justify-content-between d-flex">
                    <div className="flex-shrink-0">
                      <div className="text-muted">
                        Showing{" "}
                        <span className="fw-semibold">
                          {((pageNumber - 1) * pageSize) + 1}
                        </span>{" "}
                        to{" "}
                        <span className="fw-semibold">
                          {Math.min(pageNumber * pageSize, totalRecords)}
                        </span>{" "}
                        of <span className="fw-semibold">{totalRecords}</span>{" "}
                        Results
                      </div>
                    </div>
                    <ul className="pagination pagination-separated pagination-sm mb-0">
                      <li className={`page-item ${pageNumber === 1 ? 'disabled' : ''}`} onClick={() => GetPageData(1)}>
                        <Link className="page-link">First</Link>
                      </li>
                      <li className={`page-item ${pageNumber === 1 ? 'disabled' : ''}`} onClick={() => GetPageData(pageNumber - 1)}>
                        <Link className="page-link">Previous</Link>
                      </li>
                      {renderPageNumbers}
                      <li className={`page-item ${pageNumber === totalPages ? 'disabled' : ''}`} onClick={() => GetPageData(pageNumber + 1)}>
                        <Link className="page-link">Next</Link>
                      </li>
                      <li className={`page-item ${pageNumber === totalPages ? 'disabled' : ''}`} onClick={() => GetPageData(totalPages)}>
                        <Link className="page-link">Last</Link>
                      </li>
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Add Wallet Balance Modal */}
        <Modal
          show={walletShow}
          onHide={handleWalletModalClose}
          size="md"
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>
              Add Wallet Balance for {selectedAgent?.companyName}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="container-fluid">
              <div className="row mb-3">
                <div className="col-12">
                  Current Balance:{" "}
                  <strong className="text-success">
                    {selectedAgent?.currency || 'INR'} {selectedAgent?.walletBalance?.toFixed(2) || "0.00"}
                  </strong>
                </div>
              </div>
              <div className="row mb-3">
                <label className="col-sm-4 col-form-label">Amount to Add:</label>
                <div className="col-sm-8">
                  <input
                    type="number"
                    className={`form-control ${
                      walletErrors.amount ? "is-invalid" : ""
                    }`}
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="Enter amount"
                    autoFocus
                  />
                  {walletErrors.amount && (
                    <div className="invalid-feedback">
                      {walletErrors.amount}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <div className="d-flex justify-content-end w-100">
              <button
                className="btn btn-secondary me-2 px-4"
                onClick={handleWalletModalClose}
              >
                Cancel
              </button>
              {walletBtnLoading ? (
                <button className="btn btn-success px-4" disabled>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Saving...
                </button>
              ) : (
                <button
                  className="btn btn-success px-4"
                  onClick={handleSaveWalletBalance}
                >
                  <i className="fas fa-save me-2"></i>
                  Save
                </button>
              )}
            </div>
          </Modal.Footer>
        </Modal>
      </div>
    </>
  );
};

export default CorporateWallet;